import { API } from 'api';
import { DeepPartial, WithStringDates } from 'shared/types';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { parseGeoEventDates } from 'features/geoEvents/geoEvent.utils';

type PostGeoEventProps<TCreateUpdate = unknown> = {
  geoEventData: TCreateUpdate;
  skipRedundancyCheck?: boolean;
};

type PatchGeoEventProps<TCreateUpdate = unknown> = {
  id: number;
  geoEventData: DeepPartial<TCreateUpdate>;
};

/**
 * For prescribed burn wildfires, prescribedDateStartLocal comes back as a UTC date string,
 * but its meant to be used as a naive timezone-agnostic date. This function
 * removes UTC from that date string if present.
 *
 * @see https://trello.com/c/p9Jvym3u/1024-add-timezone-z-to-strings-returned-from-our-drf-serializers
 */
const adjustNaiveTimezones = (geoEvent: Wildfire): Wildfire => {
  if (geoEvent.geoEventType !== 'wildfire') {
    return geoEvent;
  }

  const prescribedStartLocalIso = geoEvent.data.prescribedDateStartLocal;

  if (!prescribedStartLocalIso) {
    return geoEvent;
  }

  return {
    ...geoEvent,
    data: {
      ...geoEvent.data,
      prescribedDateStartLocal: prescribedStartLocalIso.replace('Z', ''),
    },
  };
};

export const fetchGeoEvent = async <TGeoEvent = Wildfire>(
  geoEventId: number,
  cacheBusterTs: number | null,
): Promise<TGeoEvent> => {
  let url = `geo_events/${geoEventId}`;
  if (cacheBusterTs) {
    url += `?ts=${cacheBusterTs}`;
  }
  const response = await API.get<WithStringDates<Wildfire>>(url);
  return adjustNaiveTimezones(parseGeoEventDates(response.data)) as TGeoEvent;
};

export const postGeoEvent = async <TCreateUpdate = unknown>(
  props: PostGeoEventProps<TCreateUpdate>,
): Promise<TCreateUpdate> => {
  const { geoEventData, skipRedundancyCheck } = props;
  const response = await API.post(`geo_events/`, {
    ...geoEventData,
    ...(skipRedundancyCheck && { skipRedundancyCheck }),
  });

  return response.data;
};

export const patchGeoEvent = async <TCreateUpdate = unknown>(
  props: PatchGeoEventProps<TCreateUpdate>,
): Promise<TCreateUpdate> => {
  const { id, geoEventData } = props;
  const response = await API.patch(`geo_events/${id}`, geoEventData);
  return response.data;
};
